import autobind from 'autobind-decorator';
import * as React from 'react';
import {withTranslation, WithTranslation} from 'react-i18next';
import { Button, Divider, Icon, Input, Modal, Popconfirm, Row, Spin, Switch, Tag, Tooltip } from 'antd';
import {connect} from 'redux-scaffolding-ts';
import {RouteComponentProps, withRouter} from 'react-router-dom';
import {ProjectTasksStore, ProjectTaskSummary} from 'src/stores/projecttasks-store';
import {ItemState, Query} from 'src/core/stores/dataStore';
import {TableModel, TableView} from 'src/core/ui/collections/table';
import {formatMessage} from 'src/core/services/http.service';
import NewProjectTaskView from './projecttask-new';
import UpdateProjectTaskView from './projecttask-update';
import EditableTooltip from '../../../../components/editable-tooltip/tooltip';
import Checkbox from '../../../../components/custom-checkbox';
import ApplyTemplateView from '../../templates/apply-template-view';
import {notify} from 'src/core/ui/floatingNotification';
import {withIdentity} from '../../../../core/services/authentication';
import {clone} from 'src/core/utils/object';
import {UserProfileProps, withUserProfile} from "../../../../components/user-profile";


// tslint:disable-next-line:no-empty-interface
interface ProjectTasksViewProps extends RouteComponentProps<{ companyId: string, projectId: string }>, WithTranslation, UserProfileProps {
    onRefreshRequired: () => void;
    refresh: boolean;
    onRefresh: () => void;
    companyAllowCir: boolean;
    companyAllowCii: boolean;
}

interface ProjectTasksViewState {
    query: Query;
    newProjectTaskShown: boolean;
    applyTemplateShown: boolean;
    showDeleted: boolean;
    isConfirmApproveOpen: boolean;
    isEditModalProjectTaskOpen: boolean;
    dataTask : any
    isBusy?: boolean
}

@connect(['projectTasks', ProjectTasksStore])
class ProjectTasksDashboard extends React.Component<ProjectTasksViewProps, ProjectTasksViewState> {
    private get projectTasksStore() {
        return (this.props as any).projectTasks as ProjectTasksStore;
    }

    constructor(props: ProjectTasksViewProps) {
        super(props);
        this.state = {
            query: { searchQuery: '', orderBy: [{ field: 'title', direction: 'Ascending', useProfile: false }], skip: 0, take: 10 },
            newProjectTaskShown: false,
            applyTemplateShown: false,
            showDeleted: false,
            isConfirmApproveOpen: false,
            isEditModalProjectTaskOpen: false,
            dataTask: null,
            isBusy: false
        };
    }

    componentWillMount() {
        this.projectTasksStore.changeUrlByProject(this.props.match.params.companyId, this.props.match.params.projectId);
        this.load(this.state.query);
    }
    componentDidUpdate() {
        if (this.props.refresh) {
            this.load(this.state.query)
            this.props.onRefresh()
        }
    }

    @autobind
    private isReadOnly() {
        const { userProfile } = this.props;
        const readOnlyRoles = ["CompanyDataViewer"];
        const excludedRoles = ["Manager", "Administrator", "ClientAdmin", "FIConsultant"];

        return userProfile?.roles.some(role => readOnlyRoles.includes(role)) &&
            !userProfile?.roles.some(role => excludedRoles.includes(role));
    }

    @autobind
    private load(query: Query) {
        if (this.state.showDeleted)
            return this.projectTasksStore.getAllDeletedAsync(query);
        return this.projectTasksStore.getAllAsync(query);
    }

    @autobind
    private onQueryChanged(query: Query) {
        this.setState({ query }, () => this.load(query));
    }

    @autobind
    private async onSaveItem(item: ProjectTaskSummary, state: ItemState) {
        const { t } = this.props as any;
        if (item.title.length > 255) {
            notify({
                type: "error",
                autoClose: true,
                description: t("Title length cannot exceed 255 characters"),
                key: "error",
                message: t("Error"),
            })
            return
        }
        return await this.projectTasksStore.saveAsync(`${item.id}`, item, state)
            .then((result) => {
                notify({
                    type: "success",
                    autoClose: true,
                    duration: 4,
                    description: t("All changes were successfully saved"),
                    message: t("Notification"),
                    key: "success"
                })
                this.load(this.state.query);
                return result
            })
            .catch(error => {
                notify({
                    type: "error",
                    autoClose: true,
                    description: t("There was an error saving changes"),
                    key: "error",
                    message: t("Error"),
                })
                return error
            });
    }

    @autobind
    private async onDeleteRow(item: ProjectTaskSummary): Promise<unknown> {
        return new Promise((resolve, reject) => {
            this.projectTasksStore.deleteAsync(`${item.id}`).then(result => {
                if (this.props.onRefreshRequired) {
                    this.props.onRefreshRequired()
                }
                resolve(result)
            }).catch(error => {
                error.message = `${item.title} : ${error.message}`
                reject(error)
            });
        })
    }

    @autobind
    private onNewItem() {
        this.setState({ newProjectTaskShown: true });
    }

    @autobind
    private onNewItemClosed(id: string | undefined) {
        this.setState({ newProjectTaskShown: false });
        this.load(this.state.query);
    }

    @autobind
    private async setFieldValue(id: string, field: string, value: any) {
        const { t } = this.props;
        var result = await this.projectTasksStore.setEligibility(id, field, value)
            .catch(error => {
                notify({
                    type: "error",
                    autoClose: true,
                    description: t("There was an error saving changes"),
                    key: "error",
                    message: t("Error"),
                })
            })
    }

    @autobind
    private onChangedDeleted(checked: boolean) {
        var query = clone<Query>(this.state.query);
        query.skip = 0
        this.setState({ showDeleted: checked }, () => this.load(query));
    }

    @autobind
    private async softDeleteTask(item: ProjectTaskSummary) {
        await this.projectTasksStore.softDeleteTask(item.id);
        var query = clone<Query>(this.state.query);

        if (this.projectTasksStore.state.items.length == 1 && this.state.query.skip > 0) {
            query.skip = query.skip - 10
        }
        this.setState({ query }, () => this.projectTasksStore.getAllDeletedAsync(query));

    }

    @autobind
    private async onRestoreTask(item: ProjectTaskSummary) {
        await this.projectTasksStore.restoreTask(item.id);
        await this.projectTasksStore.getAllDeletedAsync(this.state.query);
    }

    private onReject = () =>
        new Promise(async (resolve, reject) => {
            this.setState({ isBusy: true });
            let companyId = this.props.match.params.companyId;
            let projectId = this.props.match.params.projectId;
            let taskId = this.state.dataTask.id;
            this.projectTasksStore.changeDeleteUrl(companyId, projectId, taskId);
            await this.projectTasksStore.delete();
            this.setState({ isConfirmApproveOpen: false, isBusy: false });
            this.load(this.state.query);
        })

    public render() {
        const { t } = this.props as any;
        let isReadOnly = this.isReadOnly()
        const tableModel = {
            query: this.state.query,
            columns: [
                {
                    sortable: true,
                    field: 'title',
                    title: t('Title'),
                    renderer: (data) => <span>
                            <Icon type="reconciliation" />&nbsp;<span>{data.title}</span>
                    </span>,
                    editor: (data) => <Input maxLength={255}/>
                },
                {
                    field: 'reference',
                    title: <EditableTooltip name='task-reference'> {t('Reference')} </EditableTooltip>,
                    renderer: (data) => <span>{data.reference}</span>,
                    editor: (data) => <Input maxLength={255}/>
                },
                {
                    field: 'sticky',
                    title: t('Sticky'),
                    align: 'center',
                    renderer: (data) => (
                        <Checkbox checked={data.sticky} disabled={(data.approved == false)} />
                    ),
                    editor: (data) => <Checkbox />
                },

                {
                    sortable: false,
                    field: 'category',
                    title: t('Category'),
                    renderer: (data) => (
                        <span> {data.category} </span>
                    ),
                    editor: (data) => <Input />
                },
                {
                    field: 'taskTemplateId',
                    title: t('Template'),
                    renderer: (data) => <span> {data.taskTemplateTitle && <Tag> {data.taskTemplateTitle} </Tag>} </span>,
                    editor: (data) => <Input hidden />
                },
                {
                    field: "actions",
                    title: "",
                    renderer: (data: ProjectTaskSummary) => (
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <Popconfirm
                                title={t("Are you sure you want to restore this task")}
                                onConfirm={() => this.onRestoreTask(data)}
                                okText={t("Yes")}
                                cancelText={t("No")}
                            >
                                <Button
                                    disabled={isReadOnly}
                                >
                                    {t('Restore')}
                                    <Icon type="info-circle" style={{ fontSize: '12px', color: 'grey', marginRight: 3, marginLeft: 3 }} />
                                </Button>
                            </Popconfirm>
                            <span style={{ margin: '0 10px' }}></span>
                            <Popconfirm
                                title={t("Are you sure you want to permanently delete this task")}
                                onConfirm={() => this.onDeleteRow(data)}
                                okText={t("Yes")}
                                cancelText={t("No")}
                            >
                                <Button
                                    disabled={isReadOnly}
                                    type="danger"
                                    style={(isReadOnly) ? {} : { color: "red", backgroundColor: "white" }}
                                >
                                    {t('Delete')}
                                    <Icon type="delete" style={(isReadOnly) ? { fontSize: '12px', marginRight: 3, marginLeft: 3 } : { fontSize: '12px', color: 'red', marginRight: 3, marginLeft: 3 }} />
                                </Button>
                            </Popconfirm>
                        </div>
                    ),
                    editor: (data: ProjectTaskSummary) => <span></span>,
                    visible: this.state.showDeleted
                },
                {
                    field: 'approved',
                    title: t('State'),
                    align: 'center',
                    renderer: (data) => {
                        if (data.approved == undefined || data.approved == true) {
                            return (<span>{t('Approved')}</span>);
                        } else if (this.props.userProfile.roles?.includes("CompanyDataViewer") && this.props.userProfile.roles?.length == 1) {
                            return (<span>{t('Pending')}</span>);
                        } else {
                            return (
                                <Tooltip placement="top" title={t('Pending approval')}>
                                    <Button
                                        style={{ backgroundColor: '#00aeff', color: 'white' }}
                                        key="back"
                                        onClick={() => this.setState({ isConfirmApproveOpen: true, dataTask: data })} >
                                        {this.props.t('Pending')}
                                    </Button>
                                </Tooltip>
                            );
                        }
                    },
                }
            ],
            data: this.projectTasksStore.state,
            sortFields: [{
                field: 'title',
                text: t('Title'),
                useProfile: false
            }],
        } as TableModel<ProjectTaskSummary>;

        if (this.props.companyAllowCii && !this.state.showDeleted) {
            tableModel.columns.splice(2, 0,
                {
                    field: 'ciiEligibility',
                    title: t('CII Eligibility'),
                    align: 'center',
                    renderer: (data) => <Checkbox disabled={!data.allowedCii || data.approved==false} checked={data.ciiEligibility} onChange={(evt: any) => { if (!isReadOnly) this.setFieldValue(data.id, 'ciiEligibility', evt.target.checked) }} />
                    ,
                    editor: (data) => <Checkbox disabled={!data.allowedCii} />
                }
            )
        }

        if (this.props.companyAllowCir && !this.state.showDeleted) {
            tableModel.columns.splice(2, 0, {
                field: 'cirEligibility',
                title: t('CIR Eligibility'),
                align: 'center',
                renderer: (data) => <Checkbox disabled={!data.allowedCir || data.approved == false} checked={data.cirEligibility} onChange={(evt: any) => { if (!isReadOnly) this.setFieldValue(data.id, 'cirEligibility', evt.target.checked) }} />,
                editor: (data) => <Checkbox disabled={!data.allowedCir} />
            })
        }
        return <Spin size='large' spinning={this.projectTasksStore.state.isBusy}>
            <Row>
                <span >
                    <EditableTooltip name="employee-list-show-deleted"><span>{t('Show Eliminated Tasks?') + ' '}</span></EditableTooltip>
                    <Switch title={t('Show Eliminated Employees?')} onChange={this.onChangedDeleted}></Switch>
                </span>
            </Row>

            <Divider />
            <TableView
                rowClassName={(record: ProjectTaskSummary, index: number) => { if (record.approved == false) { return "row-no-approved" } else return "" }}
                rowKey='id'
                model={tableModel}
                error={this.projectTasksStore.state.result && !this.projectTasksStore.state.result.isSuccess && formatMessage(this.projectTasksStore.state.result)}
                embedded={false}
                showDeleteStatusWindow
                onQueryChanged={(q: Query) => this.onQueryChanged(q)}
                onRefresh={() => this.load(this.state.query)}
                canEdit={!this.state.showDeleted && !isReadOnly}
                onSaveRow={this.onSaveItem}
                canDelete={!this.state.showDeleted && !isReadOnly}
                onDeleteRow={this.softDeleteTask}
                canCreateNew={!this.state.showDeleted && !isReadOnly}
                onNewItem={this.onNewItem}
                rightToolbar={
                    <ul>
                        <li>

                            <Button disabled={isReadOnly} onClick={() => this.setState({ applyTemplateShown: true })}> <Icon type="layout" /> {t('Apply Template')}  </Button>

                        </li>
                    </ul>}
                bulkTemplateName={t('Template Import Task List.xlsx')}
                bulkInsertUrl={this.state.showDeleted ? (isReadOnly) ? undefined : `api/v1/tasks/${this.props.match.params.companyId}/${this.props.match.params.projectId}/bulk_insert` : undefined}
                bulkTemplateUrl={this.state.showDeleted ? (isReadOnly) ? undefined : `api/v1/tasks/${this.props.match.params.companyId}/${this.props.match.params.projectId}/template` : undefined}
                showDetailedDeleteReport
            >
            </TableView>
            {this.state.newProjectTaskShown && <NewProjectTaskView companyId={this.props.match.params.companyId} projectId={this.props.match.params.projectId} onRefreshRequired={this.props.onRefreshRequired} onClose={this.onNewItemClosed} />}
            {this.state.applyTemplateShown && <ApplyTemplateView companyId={this.props.match.params.companyId} projectId={this.props.match.params.projectId} onClose={() => { this.setState({ applyTemplateShown: false }); this.load(this.state.query); }} />}

           
            <Modal
                footer={[
                    <Button type="danger" onClick={this.onReject}>{t("Reject")}</Button>,
                    <Button type="primary" onClick={() => this.setState({ isEditModalProjectTaskOpen: true, isConfirmApproveOpen: false })}>{t("Ok")}</Button>
                    ]}
                    maskClosable={true}
                    visible={this.state.isConfirmApproveOpen}
                    closable={true}
                    onCancel={() => this.setState({isConfirmApproveOpen: false })}
                    title={t('Confirmation required')}>
                    <div>
                        <p>
                            {t("Do you wish to approve the task?")}
                        </p>
                    </div>
                    <Spin spinning={this.state.isBusy}></Spin>
                 </Modal>


            {this.state.isEditModalProjectTaskOpen && <UpdateProjectTaskView
                companyId={this.props.match.params.companyId}
                projectId={this.props.match.params.projectId}
                onRefreshRequired={this.props.onRefreshRequired}
                onClose={() => this.setState({ isEditModalProjectTaskOpen: false })}
                dataTask={this.state.dataTask}
                onRefresh={() => this.load(this.state.query)}
            />
            }

        </Spin>
    }
}

// Wire up the React component to the Redux store
export default withUserProfile(withTranslation()(withIdentity(withRouter(ProjectTasksDashboard))));
